
"use client"; // Mark this as a Client Component

import Link from 'next/link';
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarTrigger,
  SidebarContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { HomeIcon, FileTextIcon, TrendingUp } from 'lucide-react'; // Changed BookOpenTextIcon to FileTextIcon for syllabus
import { Button } from '@/components/ui/button';
import AppLogo from '@/components/shared/AppLogo';
import * as React from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

export default function PageLayout({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const pathname = usePathname();
  const [user, setUser] = React.useState<any>(null);

  React.useEffect(() => {
    fetch('/api/user')
      .then(res => res.json())
      .then(data => {
        if (data.user) setUser(data.user);
      })
      .catch(() => { });
  }, []);

  return (
    <SidebarProvider>
      <Sidebar collapsible="icon" variant="sidebar" side="left">
        <SidebarHeader className="items-center justify-center p-3 border-b border-sidebar-border flex items-center">
          <Link href="/" aria-label="Home" className="flex items-center">
            <AppLogo style={{ height: '60px', width: 'auto' }} />
            <Image src="/logoComenius.png" alt="Comenius University Logo" height={120} width={120} className="ml-2 object-contain" />
          </Link>
        </SidebarHeader>
        <SidebarContent className="p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname === '/'} tooltip="Inicio">
                <Link href="/">
                  <HomeIcon /> {/* Changed icon */}
                  <span className="truncate">Home</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname.startsWith('/exam')} tooltip="Iniciar Examen">
                <Link href={pathname.startsWith('/exam') && pathname.includes('questions=') ? pathname : "/exam"}>
                  <FileTextIcon />
                  <span className="truncate">Exam</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname === '/progress'} tooltip="Ver Progreso">
                <Link href="/progress">
                  <TrendingUp />
                  <span className="truncate">Progress</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {user?.role === 'admin' && (
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={pathname === '/admin'} tooltip="Admin Panel">
                  <Link href="/admin">
                    <TrendingUp className="text-red-500" />
                    <span className="truncate text-red-500">Admin</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarContent>
        <SidebarFooter className="p-2 mt-auto border-t border-sidebar-border">
          <Button variant="outline" size="sm" className="w-full justify-start" asChild>
            <Link href="https://drive.google.com/file/d/1ciC0z9EFmz7k-XsXoFApLDWTTvwUKDZW/view?usp=sharing" target="_blank" rel="noopener noreferrer">
              <FileTextIcon /> {/* Changed icon */}
              Check Original Source {/* Changed text */}
            </Link>
          </Button>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset>
        <header className="sticky top-0 z-30 flex items-center h-14 px-4 bg-background/90 backdrop-blur-sm border-b border-border sm:px-6">
          <div className="md:hidden"> {/* SidebarTrigger only for mobile/tablet */}
            <SidebarTrigger />
          </div>
          <div className="flex-1 ml-2 text-xl font-semibold text-primary md:ml-0">
            MEDICINA POR EUROPA & COMENIUS UNIVERSITY BRATISLAVA {/* Static title for now, can be dynamic */}
          </div>
          {/* Placeholder for potential actions or user menu */}
        </header>
        <div className="flex-1 p-4 overflow-auto bg-muted/20 sm:p-6">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider >
  );
}
